import type { JSX } from 'solid-js';

const base = (d: JSX.Element, size = 16) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    {d}
  </svg>
);

export const IconFolder = () => base(<path d="M3.5 7.5a2 2 0 0 1 2-2h4l2 2h7a2 2 0 0 1 2 2v7.5a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2z" />);
export const IconChevron = (p: { open?: boolean }) => (
  <span class="chev" classList={{ open: p.open }}>
    {base(<path d="m9 6 6 6-6 6" />, 14)}
  </span>
);
export const IconSearch = () => base(<><circle cx="11" cy="11" r="6.5" /><path d="m20 20-4.2-4.2" /></>);
export const IconPlus = () => base(<path d="M12 5v14M5 12h14" />);
export const IconEdit = () => base(<path d="M4 20h4L19 9l-4-4L4 16zM13.5 6.5l4 4" />);
export const IconTrash = () => base(<path d="M4.5 7h15M9.5 7V4.5h5V7M6.5 7l1 12.5h9l1-12.5" />);
export const IconMove = () => base(<path d="M4 12h12M12 8l4 4-4 4M20 5v14" />);
export const IconGrip = () => base(<><circle cx="9" cy="6" r=".6" /><circle cx="15" cy="6" r=".6" /><circle cx="9" cy="12" r=".6" /><circle cx="15" cy="12" r=".6" /><circle cx="9" cy="18" r=".6" /><circle cx="15" cy="18" r=".6" /></>);
export const IconBookmarks = () => base(<path d="M7 3.5h8.5a2 2 0 0 1 2 2v15L12 17l-5.5 3.5v-17z" />);
export const IconPuzzle = () =>
  base(<path d="M9 4.5a2 2 0 1 1 4 0V6h4a1 1 0 0 1 1 1v4h-1.5a2 2 0 1 0 0 4H18v4a1 1 0 0 1-1 1h-4v-1.5a2 2 0 1 0-4 0V20H5a1 1 0 0 1-1-1v-4h1.5a2 2 0 1 0 0-4H4V7a1 1 0 0 1 1-1h4z" />);
export const IconDevices = () => base(<><rect x="3" y="4.5" width="14" height="10" rx="1.5" /><path d="M7 18.5h6M10 14.5v4" /><rect x="17.5" y="9" width="4" height="10.5" rx="1" /></>);
export const IconMenu = () => base(<path d="M4 7h16M4 12h16M4 17h16" />);
export const IconClose = () => base(<path d="M6 6l12 12M18 6 6 18" />);
export const IconExternal = () => base(<path d="M14 4.5h5.5V10M19.5 4.5 11 13M18 14v4.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6H10" />);
export const IconCheck = () => base(<path d="m5 12.5 4.5 4.5L19 7.5" />);
export const IconRefresh = (p: { spinning?: boolean }) => (
  <span class="spin-wrap" classList={{ spinning: p.spinning }}>
    {base(<path d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3M19.5 4.5v4h-4" />)}
  </span>
);

export function BrowserMark(p: { browser: 'chrome' | 'firefox' | 'web'; size?: number }) {
  const s = p.size ?? 16;
  if (p.browser === 'chrome') {
    return (
      <svg width={s} height={s} viewBox="0 0 24 24" aria-label="Chrome" role="img">
        <circle cx="12" cy="12" r="10" fill="#db4437" />
        <path d="M12 12 3.34 7A10 10 0 0 0 12 22z" fill="#0f9d58" />
        <path d="M12 12h8.66A10 10 0 0 1 12 22z" fill="#f4b400" />
        <circle cx="12" cy="12" r="4.4" fill="#fff" />
        <circle cx="12" cy="12" r="3.4" fill="#4285f4" />
      </svg>
    );
  }
  if (p.browser === 'firefox') {
    return (
      <svg width={s} height={s} viewBox="0 0 24 24" aria-label="Firefox" role="img">
        <circle cx="12" cy="12" r="10" fill="#ff7139" />
        <path d="M12 4.5a7.5 7.5 0 1 0 7.3 9.2c-1.2 2-3.4 3.3-5.9 3.3a5.2 5.2 0 0 1-2.1-10 6 6 0 0 0-1.1 3.4c0 .6.1 1.1.3 1.6A3 3 0 0 1 12 9c1.7 0 3.1 1.2 3.4 2.8A7.4 7.4 0 0 0 12 4.5z" fill="#ffd567" />
        <path d="M5.2 9.6A7.5 7.5 0 0 0 12 19.5a7.5 7.5 0 0 0 7.3-5.8 6.5 6.5 0 0 1-11.9-2.8 4.4 4.4 0 0 1-2.2-1.3z" fill="#9059ff" opacity=".85" />
      </svg>
    );
  }
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-label="Web" role="img">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.7 3.8 5.7 3.8 9S14.5 18.3 12 21c-2.5-2.7-3.8-5.7-3.8-9S9.5 5.7 12 3z" />
    </svg>
  );
}

export function Logo() {
  return (
    <svg width="22" height="22" viewBox="0 0 128 128" aria-hidden="true">
      <rect width="128" height="128" rx="28" fill="var(--accent)" />
      <path d="M40 26h38a12 12 0 0 1 12 12v66l-26-17-26 17V26z" fill="none" stroke="#fff" stroke-width="10" stroke-linejoin="round" />
      <path d="M53 52h24M53 66h16" stroke="#fff" stroke-width="10" stroke-linecap="round" />
    </svg>
  );
}
